import React, { FC, useContext, useMemo } from 'react';

import Lottie from 'lottie-react';

// components
import AampeTooltip from '../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../constants/aampeTheme';
import ANIMATION from '../../../constants/animation';
import ICONS from '../../../constants/icons';
import {
    ToastDisplayType,
    ToastStatus,
} from '../../../constants/toastMessage.constants';
import {
    CLIPBOARD_ACCESS_ERROR,
    CLIPBOARD_ACCESS_ERROR_MESSAGE,
    INSUFFICIENT_ROWS_ERROR_MESSAGE,
    NO_ALTERNATES,
    NO_NEW_DATA_INFO,
} from './constants';

// contexts
import { EditorContext } from '../../../context/EditorContext';

// helpers
import { sectionHeading, transformClipboardData } from './helpers';

// hooks
import useAllLabelsQuery from '../../../hooks/labels/useAllLabelsQuery';
import useVariantBulkCreateMutation from 'hooks/variants/useVariantCreateBulkMutation';
import useVariantsByMessageContentIdQuery from 'hooks/variants/useVariantsByMessageContentIdQuery';

import {
    ClipboardSection,
    GroupedAlternate,
    GroupedLabels,
    SectionTypeMasterVariant,
} from '../../../models/alternate.model';
import { ElementKind } from '../../../models/editor.model';
// models
import {
    AllVariantType,
    VariantCreateBulk,
} from '../../../models/variant.model';

// services
import { toastService } from '../../../services/toast.service';

interface StyledProps {
    disabled?: boolean;
}

const FormulaAlternateAction: FC = () => {
    const {
        editorDetails: { formula, formulaType },
    } = useContext(EditorContext);

    const { data } = useVariantsByMessageContentIdQuery(formula?.id as number);
    const { data: labels } = useAllLabelsQuery(AllVariantType.All, true);

    const createBulkAlternatesMutation = useVariantBulkCreateMutation(
        formula?.id as number,
    );

    const disabled = useMemo<boolean>(() => {
        return !formula?.structure?.some(
            (section: any) =>
                section.kind === ElementKind.section &&
                section.children.some(
                    (child: any) => child.kind === ElementKind.variant,
                ),
        );
    }, [formula]);

    const masterVariantsBySectionType = useMemo<
        Record<string, SectionTypeMasterVariant[]>
    >(() => {
        if (formula?.structure) {
            return formula?.structure?.reduce((acc, section: any) => {
                if (
                    section.kind === ElementKind.section &&
                    section.sectionType
                ) {
                    const variants = section.children
                        .filter(
                            (child: any) => child.kind === ElementKind.variant,
                        )
                        .map((variant: any) => {
                            const text = variant.children
                                .map((child: any) => child.text)
                                .join('');
                            const { variantType, id } = variant;
                            return {
                                id,
                                text,
                                variantType: variantType?.toLowerCase(),
                            };
                        });
                    if (variants.length > 0) {
                        if (!acc[section.sectionType]) {
                            acc[section.sectionType] = [];
                        }
                        acc[section.sectionType].push(...variants);
                    }
                }
                return acc;
            }, {} as Record<string, any>);
        }
        return {};
    }, [formula]);

    // Maps capitalised display name (as exported to CSV) → raw sectionType key
    const displayNameToRawSectionType = useMemo<Record<string, string>>(() => {
        if (!formula?.structure || !formulaType) return {};
        return formula.structure.reduce(
            (acc: Record<string, string>, section: any) => {
                if (section.sectionType) {
                    const display = sectionHeading(
                        section.sectionType,
                        formulaType,
                    );
                    const capitalized =
                        display.charAt(0).toUpperCase() + display.slice(1);
                    acc[capitalized] = section.sectionType;
                }
                return acc;
            },
            {},
        );
    }, [formula, formulaType]);

    const groupedAlternates = useMemo<Record<string, GroupedAlternate>>(() => {
        const result: Record<string, GroupedAlternate> = {};
        if (data) {
            data.forEach((item) => {
                const componentId = item.component_id;
                if (!result[componentId]) {
                    const originalVariant = data.find(
                        (alternate) => alternate.component_id === componentId,
                    );
                    result[componentId] = {
                        id: componentId,
                        master_variant_content:
                            originalVariant?.content as string,
                        variant_type: item.variant_type,
                        alternates: [],
                    };
                }
                result[componentId].alternates.push({
                    alternate_variant_id: item.id,
                    content: item.content,
                });
            });
            return result;
        }
        return {};
    }, [data]);

    const groupedLabels = useMemo<GroupedLabels>(() => {
        if (labels) {
            return labels?.reduce((acc, label) => {
                const { variant_type } = label;
                const _variantType = variant_type?.toLowerCase();
                if (_variantType) {
                    if (!acc[_variantType]) {
                        acc[_variantType] = [];
                    }
                    acc[_variantType].push(label);
                }
                return acc;
            }, {} as GroupedLabels);
        }
        return {};
    }, [labels]);

    const handlePaste = async () => {
        try {
            // Read text from clipboard
            const inputData = await navigator.clipboard.readText();

            // Split input data into rows and columns based on tabs and new lines
            const rows = inputData.split('\n').map((row) => row.split('\t'));

            // Check if the number of rows is sufficient
            if (rows.length <= 4) {
                toastService.showError(INSUFFICIENT_ROWS_ERROR_MESSAGE);
                return;
            }

            // Transform the clipboard data into the required format
            const transformedData = transformClipboardData(rows);
            if (!transformedData) return;
            // Initialize array to hold bulk alternates
            const bulkVariants: VariantCreateBulk[] = [];

            // Iterate over each section type and its components in the transformed data
            Object.entries(transformedData).forEach(
                ([displaySectionName, components]: [
                    string,
                    ClipboardSection[],
                ]) => {
                    // Map the display name from the CSV back to the raw sectionType used as key
                    const rawSectionType =
                        displayNameToRawSectionType[displaySectionName] ??
                        displaySectionName;
                    components.forEach((component) => {
                        // Process component type to lower case without spaces
                        const componentType = component?.type
                            ?.replaceAll(' ', '')
                            ?.toLowerCase();

                        // Get the master variant from the component's alternate labels
                        const masterVariant = component?.alternate_labels?.[0];

                        // Find an existing master variant that matches the master variant and component type
                        const existingMasterVariant =
                            masterVariantsBySectionType[rawSectionType]?.find(
                                (existingVariant) =>
                                    existingVariant.text?.trim() ===
                                        masterVariant.alternate?.trim() &&
                                    componentType?.trim() ===
                                        existingVariant.variantType?.trim(),
                            );

                        if (!existingMasterVariant) return;

                        // Get existing alternates data for the master variant
                        const existingAlternatesData =
                            groupedAlternates[existingMasterVariant.id];
                        if (!existingAlternatesData) return;

                        const {
                            alternates: existingAlternates,
                            id,
                            variant_type,
                        } = existingAlternatesData;
                        const googleSheetAlternates =
                            component?.alternate_labels;

                        // Iterate over each alternate label from the Google Sheet
                        googleSheetAlternates.forEach((copiedAlternate) => {
                            // Check if the alternate already exists
                            const alreadyExist = existingAlternates.some(
                                (existingAlternate) =>
                                    existingAlternate.content?.trim() ===
                                    copiedAlternate.alternate?.trim(),
                            );
                            if (alreadyExist) return;

                            // Find the label ID corresponding to the alternate's label
                            const label_id = groupedLabels[
                                variant_type.toLowerCase()
                            ]?.find(
                                (label) =>
                                    label.label_name?.trim() ===
                                    copiedAlternate.label?.trim(),
                            )?.id;

                            // Add the new alternate to the bulk alternates array
                            bulkVariants.push({
                                component_id: id,
                                content: copiedAlternate?.alternate,
                                label_id,
                            });
                        });
                    });
                },
            );

            // Check if there are any new alternates to add
            if (bulkVariants.length) {
                createBulkAlternatesMutation.mutate(bulkVariants);
            } else {
                toastService.showNotificationToast(
                    ToastStatus.Info,
                    NO_NEW_DATA_INFO,
                );
            }
        } catch (e: any) {
            if (e?.name === CLIPBOARD_ACCESS_ERROR) {
                toastService.showNotificationToast(
                    ToastStatus.Warning,
                    'Access blocked',
                    ToastDisplayType.Snack,
                    CLIPBOARD_ACCESS_ERROR_MESSAGE,
                );
            }
        }
    };

    return (
        <>
            <Container onClick={() => !disabled && handlePaste()}>
                <AampeTooltip
                    arrow
                    placement="top"
                    title={disabled ? NO_ALTERNATES : ''}
                >
                    <IconContainer disabled={disabled}>
                        <Icon />
                    </IconContainer>
                </AampeTooltip>
            </Container>
            {createBulkAlternatesMutation.isPending && (
                <AnimationContainer>
                    <Lottie
                        style={{
                            height: '260px',
                            width: '260px',
                        }}
                        animationData={ANIMATION.task}
                        loop={true}
                    />
                </AnimationContainer>
            )}
        </>
    );
};

export default FormulaAlternateAction;

const Container = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(2),
});

const IconContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => !['disabled'].includes(prop),
})<StyledProps>(({ disabled }) => ({
    display: 'flex',
    padding: aampeTheme.spacing4(2),
    border: `1px solid ${
        disabled
            ? aampeTheme.colors.textAccessibleWG01
            : aampeTheme.colors.solidA01
    }`,
    borderRadius: aampeTheme.borderRadius.default,
    cursor: disabled ? 'not-allowed' : 'pointer',
}));

const Icon = styled(ICONS.paste)({
    height: 20,
    width: 20,
});

const AnimationContainer = styled.div({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    zIndex: 55,
});
